import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Sparkles, Upload, FileText, X, Gauge, FlaskConical } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { FormulaVerification } from './FormulaVerification';

const DIFFICULTES = [
  { value: 'facile', label: 'Facile', desc: 'Rappel de connaissances', color: 'text-emerald-400' },
  { value: 'moyen', label: 'Moyen', desc: 'Compréhension & application', color: 'text-amber-400' },
  { value: 'difficile', label: 'Difficile', desc: 'Analyse & synthèse', color: 'text-red-400' },
];

export { DIFFICULTES };

export function QuizAIPanel({ aiSubject, setAiSubject, aiCount, setAiCount, aiLoading, difficulte, setDifficulte, uploadedDocs, uploading, onGenerate, onDropDocs, onDeleteDoc, sciMode, onToggleSciMode }) {
  const docsWithFormulas = useMemo(() => uploadedDocs.filter(d => d.formula_count > 0), [uploadedDocs]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'], 'text/plain': ['.txt'] },
    onDrop: onDropDocs
  });

  return (
    <Card className="bg-card border-border border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" /> Génération IA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="flex items-center gap-1 mb-2"><FileText className="w-3.5 h-3.5" /> Documents source (optionnel)</Label>
          <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-testid="doc-dropzone">
            <input {...getInputProps()} />
            <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{uploading ? 'Chargement...' : 'Glissez des documents ici (PDF, DOCX, TXT)'}</p>
            <p className="text-xs text-muted-foreground mt-1">L'IA créera des questions basées sur leur contenu</p>
          </div>
          {uploadedDocs.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="uploaded-docs-list">
              {uploadedDocs.map((doc) => (
                <div key={doc.doc_id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/50 group" data-testid={`doc-${doc.doc_id}`}>
                  <FileText className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.original_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(doc.char_count / 1000).toFixed(1)}k caractères
                      {doc.formula_count > 0 && <span className="ml-2 text-violet-400">• {doc.formula_count} formule{doc.formula_count > 1 ? 's' : ''} détectée{doc.formula_count > 1 ? 's' : ''}</span>}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => onDeleteDoc(doc.doc_id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0 transition-opacity" data-testid={`delete-doc-${doc.doc_id}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {/* Formula verification panels */}
              {docsWithFormulas.map(doc => (
                <FormulaVerification key={`verify-${doc.doc_id}`} docId={doc.doc_id} docName={doc.original_name} />
              ))}
            </div>
          )}
        </div>

        <div>
          <Label className="flex items-center gap-1 mb-2"><Gauge className="w-3.5 h-3.5" /> Niveau de difficulté</Label>
          <div className="grid grid-cols-3 gap-2">
            {DIFFICULTES.map(d => (
              <button key={d.value} type="button" onClick={() => setDifficulte(d.value)} className={`p-3 rounded-lg border text-left transition-all ${difficulte === d.value ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/30'}`} data-testid={`difficulte-${d.value}`}>
                <p className={`text-sm font-bold ${d.color}`}>{d.label}</p>
                <p className="text-xs text-muted-foreground">{d.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {onToggleSciMode && (
            <button
              type="button"
              data-testid="sci-mode-toggle"
              onClick={onToggleSciMode}
              className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all ${sciMode ? 'bg-violet-600/15 border border-violet-500/40 text-violet-400' : 'bg-secondary/50 border border-border text-muted-foreground hover:text-violet-400 hover:border-violet-500/30'}`}
            >
              <FlaskConical className="w-4 h-4" />
              {sciMode ? 'Mode scientifique activé — prévisualisation LaTeX / mhchem' : 'Activer le mode scientifique (formules, chimie, indices…)'}
            </button>
          )}
          <Textarea data-testid="ai-subject" className="w-full resize-y min-h-[42px]" rows={2} placeholder="Précisez ici vos consignes supplémentaires" value={aiSubject} onChange={(e) => setAiSubject(e.target.value)} />
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Nb:</Label>
              <input data-testid="ai-count" type="number" min={1} max={30} className="w-20 h-9 rounded-md border border-input bg-background px-3 text-sm" value={aiCount} onChange={(e) => setAiCount(Number(e.target.value))} />
            </div>
            <Button data-testid="ai-generate-btn" onClick={onGenerate} disabled={aiLoading} variant="secondary" className="border border-primary/30 whitespace-nowrap">
              <Sparkles className="w-4 h-4 mr-2" /> {aiLoading ? 'Génération...' : 'Générer'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
